import { useState, useRef, useEffect } from "react";
import { Input, Label } from "reactstrap";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { sortByExchangeOrder } from "src/helpers/commonHelper";
import "./ExchangeFilterDropdown.css";

interface ExchangeFilterDropdownProps {
  exchanges: { id: number; name: string }[];
  visibleIds: number[];
  onChange: (ids: number[]) => void;
  exchangeOrder: number[];
  onReorder: (ids: number[]) => void;
}

const SortableExchangeItem = ({
  exchange,
  isChecked,
  isLastChecked,
  onToggle,
}: {
  exchange: { id: number; name: string };
  isChecked: boolean;
  isLastChecked: boolean;
  onToggle: (id: number) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: exchange.id });

  const restrictedTransform = transform ? { ...transform, x: 0 } : transform;
  const style = {
    transform: CSS.Transform.toString(restrictedTransform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="exchange-filter-item exchange-filter-sortable-item">
      <div className="exchange-filter-drag-handle" {...listeners}>
        <img src="/assets/icons/drag.svg" alt="Drag" className="exchange-filter-drag-icon" />
      </div>
      <Label check className="d-flex align-items-center gap-2 mb-0 exchange-filter-label">
        <Input
          type="checkbox"
          checked={isChecked}
          disabled={isLastChecked}
          onChange={() => onToggle(exchange.id)}
        />
        <span>{exchange.name}</span>
      </Label>
    </div>
  );
};

const ExchangeFilterDropdown = ({
  exchanges,
  visibleIds,
  onChange,
  exchangeOrder,
  onReorder,
}: ExchangeFilterDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const allIds = exchanges.map((ex) => ex.id);
  const currentVisible = visibleIds.length === 0 ? allIds : visibleIds;
  const allSelected = currentVisible.length === allIds.length;

  // Apply custom order to exchanges list
  const orderedExchanges = sortByExchangeOrder(exchanges, exchangeOrder);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleToggle = (id: number) => {
    const isCurrentlyVisible = currentVisible.includes(id);
    if (isCurrentlyVisible && currentVisible.length <= 1) return;

    let updated: number[];
    if (isCurrentlyVisible) {
      updated = currentVisible.filter((v) => v !== id);
    } else {
      updated = [...currentVisible, id];
    }

    if (updated.length === allIds.length) {
      onChange([]);
    } else {
      onChange(updated);
    }
  };

  const handleToggleAll = () => {
    if (allSelected) {
      onChange([currentVisible[0]]);
    } else {
      onChange([]);
    }
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const currentIds = exchangeOrder.length > 0
      ? exchangeOrder
      : exchanges.map(ex => ex.id);

    const oldIndex = currentIds.indexOf(Number(active.id));
    const newIndex = currentIds.indexOf(Number(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    onReorder(arrayMove(currentIds, oldIndex, newIndex));
  };

  return (
    <div className="exchange-filter-wrapper" ref={dropdownRef}>
      <div
        className={`exchange-filter-trigger${isOpen ? " active" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
        title="Filter Exchanges"
      >
        <i className="fas fa-layer-group exchange-filter-icon" />
        {currentVisible.length > 1 && (
          <span className="exchange-filter-badge">{currentVisible.length}</span>
        )}
      </div>
      {isOpen && (
        <div className="exchange-filter-dropdown">
          <div className="exchange-filter-item exchange-filter-toggle-all">
            <Label check className="d-flex align-items-center gap-2 mb-0 exchange-filter-label exchange-filter-toggle-all-label">
              <Input
                type="checkbox"
                checked={allSelected}
                onChange={handleToggleAll}
              />
              <span>Select All</span>
            </Label>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedExchanges.map(ex => ex.id)}
              strategy={verticalListSortingStrategy}
            >
              {orderedExchanges.map((ex) => {
                const isChecked = currentVisible.includes(ex.id);
                const isLastChecked = isChecked && currentVisible.length <= 1;
                return (
                  <SortableExchangeItem
                    key={ex.id}
                    exchange={ex}
                    isChecked={isChecked}
                    isLastChecked={isLastChecked}
                    onToggle={handleToggle}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
};

export default ExchangeFilterDropdown;
